import { motion } from "framer-motion";

/**
 * Signature korv. squeeze-tube — soft-touch matte finish.
 *
 * Rendering notes
 * ────────────────
 * • Broad, diffused body gradient (no sharp specular) — reads as velvety matte / soft-touch.
 * • Subtle fresnel edge darkening + rim highlight for the "grippy" silicone feel.
 * • Soft radial spotlight over the wordmark to signal premium build.
 * • Dedicated highlight on the applicator/tip zone to signal the leak-proof seal.
 * • Contact ellipse + long ambient blurred shadow to ground the object.
 *
 * SVG-native — no external assets, animates through framer-motion.
 */
export default function KorvTube({
  color = "#3a2530",
  cap = "same",
  label = "korv.",
  sublabel = "LIP GLOSS",
  vol = "15 ML  /  0.5 FL OZ",
  className = "",
  animate = true,
}) {
  const capColor = cap === "dark" ? "#141212" : cap === "light" ? "#e6ddcf" : color;
  const dark = isDark(color);
  const uid = (color + capColor).replace(/#/g, "");

  const inkText   = dark ? "#EDE6D8" : "#141212";
  const softText  = dark ? "rgba(237,230,216,0.72)" : "rgba(20,18,17,0.65)";
  const microText = dark ? "rgba(237,230,216,0.55)" : "rgba(20,18,17,0.5)";

  return (
    <motion.svg
      viewBox="0 0 220 620"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      initial={animate ? { y: 40, opacity: 0 } : false}
      whileInView={animate ? { y: 0, opacity: 1 } : false}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.4, ease: [0.2, 0.7, 0.1, 1] }}
      aria-hidden="true"
    >
      <defs>
        {/* ────────── Ambient ground shadow ────────── */}
        <radialGradient id={`ground-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0"    stopColor="rgba(20,18,17,0.42)" />
          <stop offset="0.55" stopColor="rgba(20,18,17,0.18)" />
          <stop offset="1"    stopColor="rgba(20,18,17,0)" />
        </radialGradient>
        <filter id={`softblur-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6" />
        </filter>

        {/* ────────── BODY — soft-touch matte main gradient ──────────
             Broad diffused light from upper-left, ambient occlusion at right/edges.
             No hard specular. Multiple stops for velvety falloff.
        */}
        <linearGradient id={`bodyMain-${uid}`} x1="0.05" y1="0" x2="0.98" y2="0">
          <stop offset="0.00" stopColor="rgba(255,255,255,0.00)" />
          <stop offset="0.06" stopColor="rgba(255,255,255,0.18)" />
          <stop offset="0.20" stopColor="rgba(255,255,255,0.11)" />
          <stop offset="0.42" stopColor="rgba(255,255,255,0.03)" />
          <stop offset="0.60" stopColor="rgba(0,0,0,0.02)" />
          <stop offset="0.78" stopColor="rgba(0,0,0,0.14)" />
          <stop offset="0.92" stopColor="rgba(0,0,0,0.26)" />
          <stop offset="1.00" stopColor="rgba(0,0,0,0.36)" />
        </linearGradient>
        {/* Fresnel/rim — subtle brighter edge on the far left (very thin) */}
        <linearGradient id={`bodyRim-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0.00" stopColor="rgba(255,255,255,0.30)" />
          <stop offset="0.03" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="0.06" stopColor="rgba(255,255,255,0.00)" />
          <stop offset="0.95" stopColor="rgba(255,255,255,0.00)" />
          <stop offset="0.98" stopColor="rgba(255,255,255,0.04)" />
          <stop offset="1.00" stopColor="rgba(255,255,255,0.14)" />
        </linearGradient>
        {/* Vertical falloff — top and bottom slightly darker for contact/AO */}
        <linearGradient id={`bodyVert-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.00" stopColor="rgba(0,0,0,0.22)" />
          <stop offset="0.10" stopColor="rgba(0,0,0,0.02)" />
          <stop offset="0.50" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.88" stopColor="rgba(0,0,0,0.08)" />
          <stop offset="1.00" stopColor="rgba(0,0,0,0.28)" />
        </linearGradient>
        {/* Spotlight over wordmark */}
        <radialGradient id={`spot-${uid}`} cx="46%" cy="43%" r="34%" fx="42%" fy="40%">
          <stop offset="0"    stopColor="rgba(255,255,255,0.16)" />
          <stop offset="0.55" stopColor="rgba(255,255,255,0.04)" />
          <stop offset="1"    stopColor="rgba(255,255,255,0)" />
        </radialGradient>

        {/* ────────── CAP shading ────────── */}
        <linearGradient id={`capMain-${uid}`} x1="0.05" y1="0" x2="0.98" y2="0">
          <stop offset="0.00" stopColor="rgba(255,255,255,0.24)" />
          <stop offset="0.18" stopColor="rgba(255,255,255,0.09)" />
          <stop offset="0.55" stopColor="rgba(0,0,0,0)" />
          <stop offset="0.85" stopColor="rgba(0,0,0,0.20)" />
          <stop offset="1.00" stopColor="rgba(0,0,0,0.38)" />
        </linearGradient>
        {/* Cap top rim highlight — signals the "leak-proof" seal */}
        <linearGradient id={`capTopHL-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor="rgba(255,255,255,0.55)" />
          <stop offset="0.4"  stopColor="rgba(255,255,255,0.10)" />
          <stop offset="1"    stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        {/* Cap bottom joining seam — thin darker groove */}
        <linearGradient id={`capSeam-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(0,0,0,0.06)" />
          <stop offset="0.5" stopColor="rgba(0,0,0,0.28)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.05)" />
        </linearGradient>

        {/* ────────── CRIMP (base seam) ────────── */}
        <linearGradient id={`crimp-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(0,0,0,0.32)" />
          <stop offset="0.5" stopColor="rgba(0,0,0,0.10)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.36)" />
        </linearGradient>

        {/* Clip path to constrain shading to body shape */}
        <clipPath id={`bodyClip-${uid}`}>
          <path d="
            M 62 118
            C 55 132, 50 148, 46 178
            C 40 240, 38 340, 40 430
            C 41 484, 44 528, 48 558
            C 50 574, 60 580, 110 580
            C 160 580, 170 574, 172 558
            C 176 528, 179 484, 180 430
            C 182 340, 180 240, 174 178
            C 170 148, 165 132, 158 118
            Z"/>
        </clipPath>

        {/* Clip path for cap */}
        <clipPath id={`capClip-${uid}`}>
          <rect x="58" y="16" width="104" height="102" rx="4" />
        </clipPath>
      </defs>

      {/* ═══════════════ GROUND SHADOW ═══════════════ */}
      <ellipse cx="110" cy="600" rx="90" ry="10" fill={`url(#ground-${uid})`} filter={`url(#softblur-${uid})`} />
      <ellipse cx="110" cy="596" rx="60" ry="5" fill="rgba(20,18,17,0.28)" />

      {/* ═══════════════ CAP ═══════════════ */}
      <g clipPath={`url(#capClip-${uid})`}>
        {/* base fill */}
        <rect x="58" y="16" width="104" height="102" fill={capColor} />
        {/* main horizontal shading */}
        <rect x="58" y="16" width="104" height="102" fill={`url(#capMain-${uid})`} />
        {/* top rim highlight (leak-proof seal signal) */}
        <rect x="58" y="16" width="104" height="10" fill={`url(#capTopHL-${uid})`} />
        {/* subtle applicator/tip circular highlight */}
        <ellipse cx="88" cy="34" rx="26" ry="6" fill="rgba(255,255,255,0.20)" filter={`url(#softblur-${uid})`} />
      </g>
      {/* cap outer rounded border to feel like a real edge */}
      <rect x="58" y="16" width="104" height="102" rx="4" fill="none"
        stroke="rgba(0,0,0,0.20)" strokeWidth="0.6" />
      {/* cap → shoulder seam */}
      <rect x="60" y="116" width="100" height="4" fill={`url(#capSeam-${uid})`} />

      {/* ═══════════════ BODY ═══════════════ */}
      <g>
        {/* Base fill */}
        <path d="
          M 62 118
          C 55 132, 50 148, 46 178
          C 40 240, 38 340, 40 430
          C 41 484, 44 528, 48 558
          C 50 574, 60 580, 110 580
          C 160 580, 170 574, 172 558
          C 176 528, 179 484, 180 430
          C 182 340, 180 240, 174 178
          C 170 148, 165 132, 158 118
          Z"
          fill={color}
        />

        {/* Layered shading — clipped to body silhouette */}
        <g clipPath={`url(#bodyClip-${uid})`}>
          <rect x="0" y="118" width="220" height="470" fill={`url(#bodyMain-${uid})`} />
          <rect x="0" y="118" width="220" height="470" fill={`url(#bodyVert-${uid})`} />
          <rect x="0" y="118" width="220" height="470" fill={`url(#bodyRim-${uid})`} />
          {/* Soft spotlight over the wordmark area */}
          <rect x="0" y="118" width="220" height="470" fill={`url(#spot-${uid})`} />
        </g>

        {/* Fine outline for definition */}
        <path d="
          M 62 118
          C 55 132, 50 148, 46 178
          C 40 240, 38 340, 40 430
          C 41 484, 44 528, 48 558
          C 50 574, 60 580, 110 580
          C 160 580, 170 574, 172 558
          C 176 528, 179 484, 180 430
          C 182 340, 180 240, 174 178
          C 170 148, 165 132, 158 118
          Z"
          fill="none" stroke="rgba(0,0,0,0.14)" strokeWidth="0.6"
        />
      </g>

      {/* ═══════════════ CRIMP (flat sealed base) ═══════════════ */}
      <g>
        <rect x="38" y="580" width="144" height="18" fill={color} />
        <rect x="38" y="580" width="144" height="18" fill={`url(#crimp-${uid})`} />
        <line x1="38" y1="589" x2="182" y2="589" stroke="rgba(0,0,0,0.35)" strokeWidth="0.8" />
        <line x1="38" y1="591" x2="182" y2="591" stroke="rgba(255,255,255,0.06)" strokeWidth="0.4" />
      </g>

      {/* ═══════════════ WORDMARK ═══════════════ */}
      {/* Backing halo — extremely subtle, only where the wordmark sits */}
      <ellipse cx="110" cy="295" rx="60" ry="20"
        fill="rgba(255,255,255,0.05)" filter={`url(#softblur-${uid})`} />
      <text
        x="110" y="308"
        textAnchor="middle"
        fill={inkText}
        fontFamily="Fraunces, 'Cormorant Garamond', serif"
        fontWeight="500"
        fontSize="34"
        letterSpacing="-1"
        style={{ fontVariationSettings: "'opsz' 144" }}
      >
        {label}
      </text>

      {/* ═══════════════ SUBLABEL BLOCK ═══════════════ */}
      <text x="110" y="470"
        textAnchor="middle"
        fill={softText}
        fontFamily="'Inter Tight', sans-serif"
        fontWeight="500"
        fontSize="9"
        letterSpacing="2.4"
      >
        {sublabel}
      </text>
      <text x="110" y="486"
        textAnchor="middle"
        fill={microText}
        fontFamily="'Inter Tight', sans-serif"
        fontWeight="300"
        fontSize="7"
        letterSpacing="2"
      >
        BAUME À LÈVRES
      </text>
      <line x1="90" y1="500" x2="130" y2="500" stroke={microText} strokeWidth="0.4" />
      <text x="110" y="516"
        textAnchor="middle"
        fill={microText}
        fontFamily="'Inter Tight', sans-serif"
        fontWeight="300"
        fontSize="6.2"
        letterSpacing="1.8"
      >
        {vol}
      </text>
    </motion.svg>
  );
}

function isDark(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
}
